import React, { useState } from 'react';
import { X, ArrowLeft, BookOpen, Award, Users, Video, ArrowRight, Target, Brain, ShieldCheck } from 'lucide-react';

export default function EnablementPage({ onClose }) {
    const [selectedModule, setSelectedModule] = useState(null);

    const modules = [
        {
            id: 'executive-strategy',
            title: "Deep 9 Executive Strategy",
            duration: "Executive Workshop",
            audience: "Leadership",
            desc: "Architecting the Sapient Enterprise. A roadmap for leaders transitioning from digital transformation to synthetic intelligence.",
            icon: <Target size={32} />,
            fullContent: (
                <div className="space-y-12 animate-fade-in">
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 border border-amber-100">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">Strategic Metamorphosis</h3>
                        <p className="text-lg text-gray-700 leading-relaxed">
                            True adoption of Synthetic Sapience requires more than technology; it demands a fundamental restructuring of organizational intent. This program equips leaders with the framework to guide their enterprise through the transition from "human-gated" workflows to "intent-driven" outcomes.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 font-bold">1</span>
                                The Sapience Delta
                            </h4>
                            <p className="text-gray-600 leading-relaxed mb-6">
                                Defining the value gap between traditional automation and true agentic sapience. How to measure and capitalize on the "intelligence multiplier."
                            </p>

                            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 font-bold">2</span>
                                Organizational Architecture
                            </h4>
                            <p className="text-gray-600 leading-relaxed">
                                Designing teams where humans and agents collaborate as peers. Moving beyond hierarchy to network-based execution models.
                            </p>
                        </div>
                        <div>
                            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                                <h3 className="font-bold text-gray-900 mb-4">Curriculum Overview</h3>
                                <ul className="space-y-4">
                                    <li className="flex items-start gap-3 text-sm text-gray-600">
                                        <ArrowRight size={16} className="text-amber-500 mt-1 shrink-0" />
                                        <span><strong>Day 1:</strong> The Death of the Artifact &amp; Rise of Meaning</span>
                                    </li>
                                    <li className="flex items-start gap-3 text-sm text-gray-600">
                                        <ArrowRight size={16} className="text-amber-500 mt-1 shrink-0" />
                                        <span><strong>Day 2:</strong> Architecting the Intent Bus</span>
                                    </li>
                                    <li className="flex items-start gap-3 text-sm text-gray-600">
                                        <ArrowRight size={16} className="text-amber-500 mt-1 shrink-0" />
                                        <span><strong>Workshop:</strong> Recursive Strategy Simulation</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-900 text-white rounded-3xl p-8 flex flex-col md:flex-row justify-between items-center gap-6">
                        <div>
                            <h3 className="text-xl font-bold mb-2">Next Cohort</h3>
                            <p className="text-gray-400">Applications open for Q4 2025</p>
                        </div>
                        <button className="px-6 py-3 bg-white text-gray-900 rounded-xl font-bold hover:bg-gray-100 transition whitespace-nowrap">
                            Request Syllabus
                        </button>
                    </div>
                </div>
            )
        },
        {
            id: 'sapience-architect',
            title: "Certified Sapience Architect",
            duration: "4 Weeks",
            audience: "Technical Architects",
            desc: "Master the Nonary Lattice. Learn to design multi-modal agent systems, architect intent buses, and implement semantic state layers.",
            icon: <Brain size={32} />,
            fullContent: null
        },
        {
            id: 'agentic-engineering',
            title: "Agentic Engineering",
            duration: "6 Weeks",
            audience: "Senior Developers",
            desc: "Beyond prompt engineering. Build autonomous agents that plan, reason, and execute complex workflows within the Sapix OS.",
            icon: <Video size={32} />,
            fullContent: null
        },
        {
            id: 'governance-ethics',
            title: "Governance & Alignment",
            duration: "1 Week",
            audience: "Policy & Compliance",
            desc: "Deep dive into the 'Existential' layer. Building guardrails, alignment protocols, and safety audits for autonomous systems.",
            icon: <ShieldCheck size={32} />,
            fullContent: null
        }
    ];

    const currentModule = modules.find(m => m.id === selectedModule);

    return (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto animate-fade-in">
            {/* Header */}
            <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-gray-200 z-10">
                <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
                    {selectedModule ? (
                        <button
                            onClick={() => setSelectedModule(null)}
                            className="flex items-center gap-2 text-gray-700 hover:text-amber-600 transition"
                        >
                            <ArrowLeft size={20} />
                            <span className="font-medium">Back to Academy</span>
                        </button>
                    ) : (
                        <button
                            onClick={onClose}
                            className="flex items-center gap-2 text-gray-700 hover:text-amber-600 transition"
                        >
                            <ArrowLeft size={20} />
                            <span className="font-medium">Back</span>
                        </button>
                    )}

                    <button
                        onClick={onClose}
                        className="text-gray-700 hover:text-amber-600 transition"
                    >
                        <X size={24} />
                    </button>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 py-16">
                {selectedModule && currentModule ? (
                    // Detailed View
                    <div className="animate-slide-up">
                        <div className="mb-10">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-full border border-amber-200 mb-6">
                                <span className="text-amber-900 text-sm font-bold tracking-wide uppercase">{currentModule.duration} • {currentModule.audience}</span>
                            </div>
                            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                                {currentModule.title}
                            </h1>
                        </div>
                        {currentModule.fullContent || (
                            <div className="p-12 bg-gray-50 rounded-3xl text-center border border-gray-200">
                                <p className="text-gray-500 italic">Detailed curriculum for this module is currently being updated for the v2.0 framework.</p>
                            </div>
                        )}
                    </div>
                ) : (
                    // List View
                    <>
                        {/* Hero */}
                        <div className="mb-16">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full border border-amber-300 mb-6">
                                <BookOpen size={16} className="text-amber-600" />
                                <span className="text-amber-900 text-sm font-semibold">Deep 9 Academy</span>
                            </div>
                            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                                Empowering the <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">Architects of Intelligence</span>
                            </h1>
                            <p className="text-xl text-gray-600 leading-relaxed">
                                The shift to Synthetic Sapience requires a new set of skills. Deep 9 Academy provides world-class training and certification for those building the next species of software.
                            </p>
                        </div>

                        {/* Modules Grid */}
                        <div className="grid md:grid-cols-2 gap-6 mb-20">
                            {modules.map((mod, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => mod.id === 'executive-strategy' && setSelectedModule(mod.id)}
                                    className={`bg-white rounded-2xl p-8 border border-gray-200 transition-all group ${mod.id === 'executive-strategy' ? 'cursor-pointer hover:border-amber-400 hover:shadow-lg' : 'hover:border-amber-300 hover:shadow-md'}`}
                                >
                                    <div className="flex justify-between items-start mb-6">
                                        <div className="flex items-center justify-center w-12 h-12 bg-amber-50 rounded-xl text-amber-600 group-hover:bg-amber-100 transition-colors">
                                            {mod.icon}
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-amber-700 transition flex items-center gap-2">
                                        {mod.title}
                                        {mod.id === 'executive-strategy' && <ArrowRight size={18} className="text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity" />}
                                    </h3>
                                    <p className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-3">{mod.duration}</p>
                                    <p className="text-gray-600 leading-relaxed text-sm">
                                        {mod.desc}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Learning Formats */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-8 border-b border-gray-200 pb-4">Learning Formats</h2>
                            <div className="grid md:grid-cols-3 gap-8">
                                <div>
                                    <div className="flex items-center gap-3 mb-3">
                                        <Video size={20} className="text-amber-600" />
                                        <h3 className="font-bold text-gray-900">On-Demand</h3>
                                    </div>
                                    <p className="text-sm text-gray-600">Self-paced modules and interactive coding labs available 24/7.</p>
                                </div>
                                <div>
                                    <div className="flex items-center gap-3 mb-3">
                                        <Users size={20} className="text-amber-600" />
                                        <h3 className="font-bold text-gray-900">Live Cohorts</h3>
                                    </div>
                                    <p className="text-sm text-gray-600">Intensive 6-week programs with mentorship from Deep 9 engineers.</p>
                                </div>
                                <div>
                                    <div className="flex items-center gap-3 mb-3">
                                        <Award size={20} className="text-amber-600" />
                                        <h3 className="font-bold text-gray-900">Certification</h3>
                                    </div>
                                    <p className="text-sm text-gray-600">Industry-recognized credentials validating your expertise.</p>
                                </div>
                            </div>
                        </section>
                    </>
                )}
            </div>
        </div>
    );
}